/* LiftPro — shared vehicle reference loader (Make -> Model -> Generation).
 *
 * Source of truth: metaobjects make / model / generation in Shopify admin.
 * 1) If window.LiftProConfig.storefrontToken is set, the tree is built live
 *    from the Storefront API (metaobjects with PUBLIC_READ access), so vehicles
 *    added or edited in the admin appear in the picker and in the cart lists
 *    automatically — no import, no theme edits. Result is cached in
 *    localStorage for CACHE_TTL_MS.
 * 2) Otherwise (or on any error) it falls back to the static asset
 *    fitment-tree.json generated by tools/convert.py.
 *
 * Tree shape (same as fitment-tree.json):
 *   { makes: [ { n, h, models: [ { n, h, gens: [ { n, h, y1, y2 } ] } ] } ] }
 */
(function () {
  "use strict";

  var CACHE_KEY = "liftpro_vehicle_tree_v1";
  var CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6 hours
  var API_VERSION = "2026-07";
  var PAGE = 250;

  var cfg = window.LiftProConfig || {};

  function readCache() {
    try {
      var raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var obj = JSON.parse(raw);
      if (!obj || !obj.t || !obj.tree) return null;
      if (Date.now() - obj.t > CACHE_TTL_MS) return null;
      if (obj.token !== (cfg.storefrontToken || "")) return null;
      return obj.tree;
    } catch (e) { return null; }
  }

  function writeCache(tree) {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({
        t: Date.now(), token: cfg.storefrontToken || "", tree: tree
      }));
    } catch (e) { /* quota / private mode */ }
  }

  function sfQuery(query, variables) {
    return fetch("/api/" + API_VERSION + "/graphql.json", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": cfg.storefrontToken
      },
      body: JSON.stringify({ query: query, variables: variables || {} })
    }).then(function (r) { return r.json(); }).then(function (j) {
      if (j.errors && j.errors.length) throw new Error(j.errors[0].message);
      return j.data;
    });
  }

  function fetchAll(type, fieldsFragment) {
    var out = [];
    function page(after) {
      var q = "query($type:String!,$first:Int!,$after:String){" +
        "metaobjects(type:$type,first:$first,after:$after){" +
        "pageInfo{hasNextPage endCursor} nodes{handle " + fieldsFragment + "}}}";
      return sfQuery(q, { type: type, first: PAGE, after: after }).then(function (d) {
        var m = d.metaobjects;
        out = out.concat(m.nodes);
        if (m.pageInfo.hasNextPage) return page(m.pageInfo.endCursor);
        return out;
      });
    }
    return page(null);
  }

  function fieldVal(node, key) {
    for (var i = 0; i < (node.fields || []).length; i++) {
      if (node.fields[i].key === key) return node.fields[i];
    }
    return null;
  }

  function buildFromStorefront() {
    var F_NAME = "fields{key value reference{... on Metaobject{handle}}}";
    return Promise.all([
      fetchAll("make", F_NAME),
      fetchAll("model", F_NAME),
      fetchAll("generation", F_NAME)
    ]).then(function (res) {
      var makes = {}, order = [];
      res[0].forEach(function (mk) {
        var name = fieldVal(mk, "name");
        makes[mk.handle] = { n: name ? name.value : mk.handle, h: mk.handle, models: [] };
        order.push(mk.handle);
      });
      var models = {};
      res[1].forEach(function (md) {
        var name = fieldVal(md, "name");
        var mkRef = fieldVal(md, "make");
        var mkH = mkRef && mkRef.reference ? mkRef.reference.handle : null;
        if (!mkH || !makes[mkH]) return;
        var node = { n: name ? name.value : md.handle, h: md.handle, gens: [] };
        models[md.handle] = node;
        makes[mkH].models.push(node);
      });
      res[2].forEach(function (g) {
        var name = fieldVal(g, "name");
        var y1 = fieldVal(g, "year_from"), y2 = fieldVal(g, "year_to");
        var mdRef = fieldVal(g, "model");
        var mdH = mdRef && mdRef.reference ? mdRef.reference.handle : null;
        if (!mdH || !models[mdH]) return;
        var a = y1 && y1.value ? parseInt(y1.value, 10) : null;
        var b = y2 && y2.value ? parseInt(y2.value, 10) : null;
        var nm = name && name.value ? name.value.trim() : "";
        var years = (a || b) ? ((a || "") + "-" + (b || "")) : "";
        // name из справочника обычно уже полный ("(II) 2001-2003"); дополняем годами
        // только если их там нет
        var label = /\d{4}/.test(nm) ? nm : (nm ? ("(" + nm + ")" + (years ? " " + years : "")) : years);
        models[mdH].gens.push({ n: label || g.handle, h: g.handle, y1: a, y2: b });
      });
      var byName = function (x, y) { return x.n.localeCompare(y.n); };
      var tree = { makes: order.map(function (h) { return makes[h]; }).sort(byName) };
      tree.makes.forEach(function (mk) {
        mk.models.sort(byName);
        mk.models.forEach(function (md) {
          md.gens.sort(function (x, y) { return (x.y1 || 0) - (y.y1 || 0) || x.n.localeCompare(y.n); });
        });
      });
      if (!tree.makes.length) throw new Error("empty tree from Storefront API");
      return tree;
    });
  }

  function loadStatic() {
    var url = cfg.treeUrl || "/cdn/shop/t/1/assets/fitment-tree.json";
    return fetch(url).then(function (r) { return r.json(); });
  }

  var promise = null;
  function load() {
    if (promise) return promise;
    var cached = readCache();
    if (cached) {
      promise = Promise.resolve(cached);
      return promise;
    }
    if (cfg.storefrontToken) {
      promise = buildFromStorefront().then(function (tree) {
        writeCache(tree);
        return tree;
      }).catch(function (e) {
        console.warn("LiftPro vehicles: Storefront API failed, using static tree", e);
        return loadStatic();
      });
    } else {
      promise = loadStatic();
    }
    return promise;
  }

  window.LiftProVehicles = { load: load, clearCache: function () {
    try { localStorage.removeItem(CACHE_KEY); } catch (e) {}
  } };
})();
